
import { useTranslation } from 'react-i18next';
import { useRef, useEffect, useState } from 'react';

export default function About() {
  const { t } = useTranslation();
  const sectionRef = useRef<HTMLElement>(null);
  const [isVisible, setIsVisible] = useState(false);

  // Intersection Observer for fade-in effect
  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.1 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, []);
  
  const features = [
    t('about.feature1'),
    t('about.feature2'),
    t('about.feature3'),
    t('about.feature4'),
    t('about.feature5'),
    t('about.feature6')
  ];

  return (
    <section 
      ref={sectionRef}
      id="about" 
      className={`py-12 md:py-24 bg-[#0F172A] transition-all duration-1000 ${
        isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 md:px-6 lg:px-8">
        <div className="grid lg:grid-cols-2 gap-8 md:gap-12 items-center">
          {/* Logo - Hidden on mobile, shown on desktop */}
          <div className={`relative hidden lg:block transition-all duration-700 delay-300 ${
            isVisible ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-10'
          }`}>
            <div className="relative z-10 rounded-2xl overflow-hidden bg-white/5 backdrop-blur-sm p-12 flex items-center justify-center">
              <img 
                src="https://static.readdy.ai/image/90e3b7d98f6a66a16793c7597f1aaf38/28355f2063df5ee844bb8591b15f95b6.jpeg" 
                alt="BRAX Facility Group"
                className="w-full max-w-md h-auto object-contain"
              />
            </div>
            <div className="absolute top-4 left-4 w-20 h-20 bg-gradient-to-br from-[#3B82F6] to-[#60A5FA] rounded-2xl -z-10"></div>
            <div className="absolute bottom-4 right-4 w-20 h-20 bg-gradient-to-br from-[#60A5FA] to-[#3B82F6] rounded-2xl -z-10"></div>
          </div>

          <div className={`transition-all duration-700 delay-200 ${
            isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
          }`}>
            {/* Mobile: Logo with clean design */}
            <div className="flex items-center gap-4 mb-5 lg:hidden">
              <div className="relative">
                <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-[#3B82F6] to-[#60A5FA] p-0.5">
                  <div className="w-full h-full rounded-[10px] bg-[#0F172A] flex items-center justify-center overflow-hidden">
                    <img 
                      src="https://static.readdy.ai/image/90e3b7d98f6a66a16793c7597f1aaf38/28355f2063df5ee844bb8591b15f95b6.jpeg" 
                      alt="BRAX Facility Group"
                      className="w-10 h-10 object-contain"
                    />
                  </div>
                </div>
              </div>
              <div>
                <span className="inline-block px-3 py-1 bg-gradient-to-r from-[#3B82F6]/20 to-[#60A5FA]/20 text-[#60A5FA] text-xs font-medium rounded-full">
                  {t('about.badge')}
                </span>
              </div>
            </div>

            {/* Desktop badge */}
            <span className="hidden lg:inline-block px-4 py-1.5 bg-white/10 text-white text-sm font-medium rounded-full mb-4">
              {t('about.badge')}
            </span>

            <h2 className="text-2xl md:text-3xl lg:text-4xl font-bold text-white mb-4 md:mb-6">
              {t('about.title')}
            </h2>
            <p className="text-gray-300 text-sm md:text-base mb-4 md:mb-5 leading-relaxed">
              {t('about.description1')}
            </p>
            <p className="text-gray-300 text-xs md:text-sm mb-6 md:mb-8 leading-relaxed hidden md:block">
              {t('about.description2')}
            </p>

            <div className="grid grid-cols-2 gap-2 md:gap-3">
              {features.map((feature, index) => (
                <div 
                  key={index} 
                  className={`flex items-center gap-2 md:gap-3 transition-all duration-500 ${
                    isVisible ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-4'
                  }`}
                  style={{ transitionDelay: isVisible ? `${400 + index * 100}ms` : '0ms' }}
                >
                  <div className="w-4 h-4 md:w-5 md:h-5 rounded-full bg-gradient-to-br from-[#3B82F6] to-[#60A5FA] flex items-center justify-center flex-shrink-0">
                    <i className="ri-check-line text-white text-[10px] md:text-xs"></i>
                  </div>
                  <span className="text-gray-200 text-xs md:text-sm">{feature}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
